import React, { useState, useEffect } from 'react';
    import { motion } from 'framer-motion';
    import { supabase } from '../../config/supabase';
    import SafeIcon from '../../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiCheck, FiX, FiExternalLink, FiTrash2 } = FiIcons;

    const PendingTab = () => {
      const [pendingProfiles, setPendingProfiles] = useState([]);
      const [loading, setLoading] = useState(true);
      const [selectedProfiles, setSelectedProfiles] = useState([]);

      useEffect(() => {
        fetchPendingProfiles();
      }, []);

      const fetchPendingProfiles = async () => {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('status', 'pending')
            .order('created_at', { ascending: false });
          if (error) throw error;
          setPendingProfiles(data || []);
        } catch (error) {
          console.error('Error fetching pending profiles:', error);
        } finally {
          setLoading(false);
        }
      };

      const handleApprove = async (profileId) => {
        try {
          const { error } = await supabase
            .from('profiles')
            .update({ status: 'approved' })
            .eq('id', profileId);
          if (error) throw error;
          fetchPendingProfiles();
        } catch (error) {
          console.error('Error approving profile:', error);
        }
      };

      const handleReject = async (profileId) => {
        if (window.confirm('Are you sure you want to reject this profile?')) {
          try {
            const { error } = await supabase
              .from('profiles')
              .delete()
              .eq('id', profileId);
            if (error) throw error;
            fetchPendingProfiles();
          } catch (error) {
            console.error('Error rejecting profile:', error);
          }
        }
      };

      const handleBulkDelete = async () => {
        if (selectedProfiles.length === 0) return;
        if (
          window.confirm(
            `Are you sure you want to delete ${selectedProfiles.length} profiles? This will permanently reject them.`
          )
        ) {
          try {
            const { error } = await supabase
              .from('profiles')
              .delete()
              .in('id', selectedProfiles);
            if (error) throw error;
            setSelectedProfiles([]);
            fetchPendingProfiles();
          } catch (error) {
            console.error('Error deleting profiles:', error);
          }
        }
      };

      const toggleProfileSelection = (profileId) => {
        setSelectedProfiles((prev) =>
          prev.includes(profileId)
            ? prev.filter((id) => id !== profileId)
            : [...prev, profileId]
        );
      };

      const toggleSelectAll = () => {
        if (selectedProfiles.length === pendingProfiles.length) {
          setSelectedProfiles([]);
        } else {
          setSelectedProfiles(pendingProfiles.map((profile) => profile.id));
        }
      };

      if (loading) {
        return (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        );
      }

      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Pending Review ({pendingProfiles.length})
            </h2>
            {selectedProfiles.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                <SafeIcon icon={FiTrash2} />
                <span>Delete ({selectedProfiles.length})</span>
              </button>
            )}
          </div>
          {pendingProfiles.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No pending profiles</h3>
              <p className="text-gray-600">All profiles have been reviewed</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={pendingProfiles.length > 0 && selectedProfiles.length === pendingProfiles.length}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Handle</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Bio</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Tags</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pendingProfiles.map((profile) => (
                      <motion.tr
                        key={profile.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedProfiles.includes(profile.id)}
                            onChange={() => toggleProfileSelection(profile.id)}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{profile.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                           <a href={`https://instagram.com/${profile.instagram_handle}`} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 hover:text-purple-600">
                             <span>@{profile.instagram_handle}</span>
                             <SafeIcon icon={FiExternalLink} className="text-xs"/>
                           </a>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={profile.bio}>
                          {profile.bio}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(profile.tags || []).slice(0, 3).map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                             {(profile.tags || []).length > 3 && (
                                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                                    +{(profile.tags || []).length - 3}
                                </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleApprove(profile.id)}
                              className="p-2 text-green-600 hover:text-green-700 rounded-md hover:bg-green-50 transition-colors"
                              title="Approve"
                            >
                              <SafeIcon icon={FiCheck} />
                            </button>
                            <button
                              onClick={() => handleReject(profile.id)}
                              className="p-2 text-red-600 hover:text-red-700 rounded-md hover:bg-red-50 transition-colors"
                              title="Reject"
                            >
                              <SafeIcon icon={FiX} />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      );
    };

    export default PendingTab;